import { useRef, useState } from "react";
import { uniqueId } from "lodash";
import { t } from "@/i18n-js/instance";
import type { MarketingEmailTemplateContentType } from "@/react/types/MarketingEmailTemplates";
import { Loader } from "@circle-react-uikit/Loader";
import { Typography } from "@circle-react-uikit/Typography";
import { useTemplatesInfiniteQuery } from "../hooks/useTemplatesInfiniteQuery";
import { useIsListSystemTemplatesEnabled } from "../useIsListSystemTemplatesEnabled";
import { Placeholder } from "./Placeholder";
import type { ContentType } from "./TemplateFilter";
import { TemplateList } from "./TemplateList";

const i18nRoot = "settings.emails.templates";

export const SELECTED_CATEGORY_TO_TEMPLATE_TYPE_MAP = {
  saved_templates: "user",
  all: "all",
  circle_templates: "system",
};

export type SelectedCategory =
  keyof typeof SELECTED_CATEGORY_TO_TEMPLATE_TYPE_MAP;

export const TemplateContents = ({
  selectedCategory,
  onApply,
}: {
  selectedCategory: SelectedCategory;
  onApply: (
    templateId: number,
    contentType: MarketingEmailTemplateContentType,
  ) => void;
}) => {
  const scrollableNodeIdRef = useRef(
    uniqueId("marketing-email-list-templates-body-"),
  );
  const [contentType, setContentType] = useState<ContentType>("all");
  const { templates, totalCount, fetchNextPage, hasNextPage, isLoading } =
    useTemplatesInfiniteQuery({
      params: {
        page: 1,
        per_page: 20,
      },
      contentType: contentType,
      templateType: SELECTED_CATEGORY_TO_TEMPLATE_TYPE_MAP[selectedCategory],
    });

  const isListSystemTemplatesEnabled = useIsListSystemTemplatesEnabled();

  if (isLoading) {
    return <Loader />;
  }

  if (
    selectedCategory === "circle_templates" &&
    !isListSystemTemplatesEnabled
  ) {
    return (
      <div className="flex w-full flex-col gap-4 overflow-y-auto px-8">
        <Typography.TitleMd>
          {t([i18nRoot, selectedCategory])}
        </Typography.TitleMd>
        <Placeholder
          title={t(`${i18nRoot}.coming_soon_with_category`, {
            category: t([i18nRoot, selectedCategory]),
          })}
          description={t([i18nRoot, "coming_soon_description"])}
        />
      </div>
    );
  }

  const renderTemplateList = () => (
    <TemplateList
      scrollableNodeIdRef={scrollableNodeIdRef}
      fetchNextPage={fetchNextPage}
      hasNextPage={Boolean(hasNextPage)}
      templates={templates}
      totalCount={totalCount || 0}
      selectedContentType={contentType}
      setSelectedContentType={setContentType}
      onApply={onApply}
      selectedCategory={selectedCategory}
    />
  );

  return (
    <div className="flex w-full flex-col gap-4 overflow-y-auto px-8">
      <Typography.TitleMd>{t([i18nRoot, selectedCategory])}</Typography.TitleMd>
      {renderTemplateList()}
    </div>
  );
};
